const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(
  response,
  label
) {
  const body =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ` +
      `${body || response.statusText}`
    );
  }

  return body
    ? JSON.parse(body)
    : [];
}

function inFilter(values) {
  return (
    '(' +
    [
      ...new Set(
        (values || [])
          .filter(Boolean)
          .map(String)
      )
    ]
      .map(encodeURIComponent)
      .join(',') +
    ')'
  );
}

function addSignedDays(
  dateStr,
  days
) {
  if (
    !dateStr ||
    days === null ||
    days === undefined ||
    days === '' ||
    !Number.isFinite(Number(days))
  ) {
    return null;
  }

  const parts =
    String(dateStr)
      .slice(0, 10)
      .split('-')
      .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return null;
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  date.setDate(
    date.getDate() +
      Number(days)
  );

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, '0'),
    String(
      date.getDate()
    ).padStart(2, '0')
  ].join('-');
}

function addExtensionDays(
  dateStr,
  days
) {
  if (!dateStr) {
    return null;
  }

  const extensionDays =
    Math.max(
      0,
      Number(days) || 0
    );

  return extensionDays > 0
    ? addSignedDays(
        dateStr,
        extensionDays
      )
    : String(dateStr).slice(0, 10);
}

function getScheduleSource(
  item,
  readings,
  events
) {
  if (!item) {
    return {
      label: '',
      anchorDate: null
    };
  }

  if (
    item.item_type ===
    'reading'
  ) {
    const occurrence =
      (readings || []).find(
        row =>
          String(row.id) ===
          String(
            item.week_reading_assignment_id
          )
      );

    const week =
      occurrence?.course_weeks;

    const reading =
      occurrence?.course_readings;

    return {
      label: [
        week?.week_number
          ? `Week ${week.week_number}`
          : null,
        reading?.title ||
          'Reading',
        reading?.author
          ? `by ${reading.author}`
          : null
      ]
        .filter(Boolean)
        .join(' · '),

      anchorDate:
        week?.class_date ||
        null
    };
  }

  const event =
    (events || []).find(
      row =>
        String(row.id) ===
        String(
          item.course_event_id
        )
    );

  const week =
    event?.course_weeks;

  return {
    label: [
      week?.week_number
        ? `Week ${week.week_number}`
        : null,
      event?.title ||
        'Event',
      event?.event_time
        ? String(
            event.event_time
          ).slice(0, 5)
        : null,
      event?.location ||
        null
    ]
      .filter(Boolean)
      .join(' · '),

    anchorDate:
      event?.event_date ||
      week?.class_date ||
      null
  };
}

function getAssignmentLabel(
  assignment,
  structuredAssignments,
  settingsByType
) {
  const setting =
    settingsByType.get(
      String(
        assignment.assignment_type_id
      )
    );

  const base =
    String(
      assignment
        ?.assignment_display_name ||
      setting?.display_name ||
      ''
    ).trim() ||
    assignment
      ?.assignment_types
      ?.name ||
    'Assignment';

  const sameTypeCount =
    structuredAssignments.filter(
      candidate =>
        String(
          candidate.assignment_type_id
        ) ===
        String(
          assignment.assignment_type_id
        )
    ).length;

  return (
    base +
    (
      sameTypeCount > 1
        ? ` ${assignment.instance_number}`
        : ''
    )
  );
}

export async function
loadStudentStructuredFormCheckpoints({
  courseId,
  enrolment,
  assignments,
  assignmentTypeSettings,
  isMaster = false
}) {
  const structuredAssignments =
    (Array.isArray(assignments)
      ? assignments
      : []
    ).filter(
      assignment =>
        assignment
          ?.assignment_types
          ?.assignment_kind ===
        'structured_form'
    );

  if (
    !courseId ||
    structuredAssignments.length ===
      0
  ) {
    return [];
  }

  const assignmentIds =
    structuredAssignments.map(
      assignment =>
        assignment.id
    );

  const assignmentFilter =
    inFilter(
      assignmentIds
    );

  const membershipPromise =
    enrolment?.id
      ? fetch(
          `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${encodeURIComponent(enrolment.id)}&course_assignment_id=in.${assignmentFilter}&select=course_assignment_id,enrolment_id,group_number`,
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form group membership'
          )
        )
      : Promise.resolve([]);

  const [
    instances,
    memberships,
    workspaces
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instances?course_assignment_id=in.${assignmentFilter}&select=id,course_assignment_id,instance_order,open_date,close_date,schedule_open_offset_days,schedule_close_offset_days&order=instance_order.asc`,
      { headers: headers() }
    ).then(response =>
      readJson(
        response,
        'Loading Structured Form instances'
      )
    ),

    membershipPromise,

    fetch(
      `${SUPABASE_URL}/rest/v1/structured_form_group_workspaces?course_assignment_id=in.${assignmentFilter}&select=course_assignment_id,group_number,owner_enrolment_id`,
      { headers: headers() }
    ).then(response =>
      readJson(
        response,
        'Loading Structured Form group workspaces'
      )
    )
  ]);

  const instanceRows =
    Array.isArray(instances)
      ? instances
      : [];

  if (!instanceRows.length) {
    return [];
  }

  const instanceFilter =
    inFilter(
      instanceRows.map(
        row => row.id
      )
    );

  const [
    groupDeadlines,
    scheduleItems,
    scheduleSelections
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?course_structured_instance_id=in.${instanceFilter}&select=*`,
      { headers: headers() }
    ).then(response =>
      readJson(
        response,
        'Loading Structured Form group dates'
      )
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?course_structured_instance_id=in.${instanceFilter}&select=*&order=display_order.asc,created_at.asc`,
      { headers: headers() }
    ).then(response =>
      readJson(
        response,
        'Loading Structured Form Schedule anchors'
      )
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?course_structured_instance_id=in.${instanceFilter}&select=*`,
      { headers: headers() }
    ).then(response =>
      readJson(
        response,
        'Loading Structured Form Schedule choices'
      )
    )
  ]);

  const scheduleItemRows =
    Array.isArray(scheduleItems)
      ? scheduleItems
      : [];

  const readingIds =
    scheduleItemRows
      .map(
        row =>
          row.week_reading_assignment_id
      )
      .filter(Boolean);

  const eventIds =
    scheduleItemRows
      .map(
        row =>
          row.course_event_id
      )
      .filter(Boolean);

  const readingPromise =
    readingIds.length
      ? fetch(
          `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?id=in.${inFilter(readingIds)}&select=id,course_week_id,course_readings(id,title,author),course_weeks(id,week_number,class_date)`,
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form Schedule readings'
          )
        )
      : Promise.resolve([]);

  const eventPromise =
    eventIds.length
      ? fetch(
          `${SUPABASE_URL}/rest/v1/course_events?id=in.${inFilter(eventIds)}&select=id,title,event_date,event_time,location,course_week_id,course_weeks(id,week_number,class_date)`,
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form Schedule events'
          )
        )
      : Promise.resolve([]);

  const settingsByType =
    new Map(
      (
        Array.isArray(
          assignmentTypeSettings
        )
          ? assignmentTypeSettings
          : []
      ).map(row => [
        String(
          row.assignment_type_id
        ),
        row
      ])
    );

  const membershipByAssignment =
    new Map(
      (
        Array.isArray(memberships)
          ? memberships
          : []
      ).map(row => [
        String(
          row.course_assignment_id
        ),
        row
      ])
    );

  const workspaceByAssignmentAndGroup =
    new Map(
      (
        Array.isArray(workspaces)
          ? workspaces
          : []
      ).map(row => [
        `${row.course_assignment_id}|${row.group_number}`,
        row
      ])
    );

  const ownerPairs =
    [];

  structuredAssignments.forEach(
    assignment => {
      const usesGroups =
        assignment.groups_enabled ===
          true &&
        Number(
          assignment.group_count
        ) > 0 &&
        !isMaster;

      if (!usesGroups) {
        if (enrolment?.id) {
          ownerPairs.push({
            assignmentId:
              assignment.id,
            enrolmentId:
              enrolment.id
          });
        }

        return;
      }

      const membership =
        membershipByAssignment.get(
          String(
            assignment.id
          )
        );

      if (!membership) {
        return;
      }

      const workspace =
        workspaceByAssignmentAndGroup.get(
          `${assignment.id}|${membership.group_number}`
        );

      if (
        workspace
          ?.owner_enrolment_id
      ) {
        ownerPairs.push({
          assignmentId:
            assignment.id,
          enrolmentId:
            workspace
              .owner_enrolment_id
        });
      }
    }
  );

  const ownerEnrolmentIds =
    [
      ...new Set(
        ownerPairs
          .map(
            row =>
              row.enrolmentId
          )
          .filter(Boolean)
      )
    ];

  const parentSubmissionsPromise =
    ownerEnrolmentIds.length
      ? fetch(
          `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=in.${inFilter(ownerEnrolmentIds)}&course_assignment_id=in.${assignmentFilter}&course_assignment_schedule_item_id=is.null&structured_form_instance_id=is.null&select=enrolment_id,course_assignment_id,extension_days,structured_form_instance_extensions`,
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form extensions'
          )
        )
      : Promise.resolve([]);

  const [
    readings,
    events,
    parentSubmissions
  ] = await Promise.all([
    readingPromise,
    eventPromise,
    parentSubmissionsPromise
  ]);

  const extensionByAssignmentAndEnrolment =
    new Map(
      (
        Array.isArray(
          parentSubmissions
        )
          ? parentSubmissions
          : []
      ).map(row => [
        `${row.course_assignment_id}|${row.enrolment_id}`,
        row
      ])
    );

  const assignmentById =
    new Map(
      structuredAssignments.map(
        assignment => [
          String(
            assignment.id
          ),
          assignment
        ]
      )
    );

  return instanceRows
    .map(instance => {
      const assignment =
        assignmentById.get(
          String(
            instance
              .course_assignment_id
          )
        );

      if (!assignment) {
        return null;
      }

      const setting =
        settingsByType.get(
          String(
            assignment
              .assignment_type_id
          )
        ) || {};

      const usesGroups =
        assignment.groups_enabled ===
          true &&
        Number(
          assignment.group_count
        ) > 0 &&
        !isMaster;

      const membership =
        usesGroups
          ? membershipByAssignment.get(
              String(
                assignment.id
              )
            ) || null
          : null;

      if (
        usesGroups &&
        !membership
      ) {
        return {
          id:
            `structured-${instance.id}`,
          structured_form_checkpoint:
            true,
          structured_form_instance_id:
            instance.id,
          structured_form_instance_order:
            instance.instance_order,
          course_assignment_id:
            assignment.id,
          course_id:
            assignment.course_id ||
            courseId,
          assignment_type_id:
            assignment.assignment_type_id,
          assignment_types:
            assignment.assignment_types,
          assignment_display_name:
            assignment
              .assignment_display_name,
          instance_number:
            assignment.instance_number,
          effective_sequence_id:
            setting.sequence_id ||
            null,
          student_assignment_timing_resolved:
            true,
          student_assignment_applicable:
            false,
          open_date: null,
          due_date: null,
          effective_open_date: null,
          effective_due_date: null,
          label:
            `${getAssignmentLabel(
              assignment,
              structuredAssignments,
              settingsByType
            )} — Part ${instance.instance_order}`
        };
      }

      const groupNumber =
        membership
          ?.group_number ||
        null;

      const workspace =
        groupNumber
          ? workspaceByAssignmentAndGroup.get(
              `${assignment.id}|${groupNumber}`
            ) || null
          : null;

      const ownerEnrolmentId =
        usesGroups
          ? workspace
              ?.owner_enrolment_id ||
            null
          : enrolment?.id ||
            null;

      const extensionContext =
        ownerEnrolmentId
          ? extensionByAssignmentAndEnrolment.get(
              `${assignment.id}|${ownerEnrolmentId}`
            ) || null
          : null;

      const assignmentExtensionDays =
        Math.max(
          0,
          Number(
            extensionContext
              ?.extension_days
          ) || 0
        );

      const instanceExtensionDays =
        Math.max(
          0,
          Number(
            extensionContext
              ?.structured_form_instance_extensions
              ?.[String(instance.id)]
          ) || 0
        );

      const extensionDays =
        Math.max(
          assignmentExtensionDays,
          instanceExtensionDays
        );

      const scheduleEnabled =
        setting.schedule_enabled ===
        true;

      const choiceEnabled =
        scheduleEnabled &&
        setting
          .schedule_choice_enabled ===
        true;

      let openDate = null;
      let baseDueDate = null;
      let scheduleItem = null;
      let scheduleSource = {
        label: '',
        anchorDate: null
      };
      let choiceMissing = false;

      if (scheduleEnabled) {
        const mode =
          choiceEnabled
            ? 'choice'
            : 'single';

        const instanceItems =
          scheduleItemRows
            .filter(
              item =>
                String(
                  item.course_structured_instance_id
                ) ===
                  String(
                    instance.id
                  ) &&
                item.configuration_mode ===
                  mode
            )
            .sort(
              (a, b) =>
                (Number(
                  a.display_order
                ) || 0) -
                  (Number(
                    b.display_order
                  ) || 0) ||
                String(
                  a.id
                ).localeCompare(
                  String(b.id)
                )
            );

        if (choiceEnabled) {
          let selection = null;

          if (isMaster) {
            scheduleItem =
              instanceItems[0] ||
              null;
          } else if (
            groupNumber
          ) {
            selection =
              (
                Array.isArray(
                  scheduleSelections
                )
                  ? scheduleSelections
                  : []
              ).find(
                row =>
                  String(
                    row.course_structured_instance_id
                  ) ===
                    String(
                      instance.id
                    ) &&
                  row.enrolment_id ===
                    null &&
                  Number(
                    row.group_number
                  ) ===
                    Number(
                      groupNumber
                    )
              ) || null;
          } else {
            selection =
              (
                Array.isArray(
                  scheduleSelections
                )
                  ? scheduleSelections
                  : []
              ).find(
                row =>
                  String(
                    row.course_structured_instance_id
                  ) ===
                    String(
                      instance.id
                    ) &&
                  String(
                    row.enrolment_id
                  ) ===
                    String(
                      enrolment?.id ||
                      ''
                    ) &&
                  row.group_number ===
                    null
              ) || null;
          }

          if (
            !scheduleItem &&
            selection
          ) {
            scheduleItem =
              instanceItems.find(
                item =>
                  String(
                    item.id
                  ) ===
                  String(
                    selection
                      .course_structured_instance_schedule_item_id
                  )
              ) || null;
          }

          choiceMissing =
            !scheduleItem;
        } else {
          scheduleItem =
            instanceItems[0] ||
            null;
        }

        scheduleSource =
          getScheduleSource(
            scheduleItem,
            readings,
            events
          );

        openDate =
          addSignedDays(
            scheduleSource
              .anchorDate,
            instance
              .schedule_open_offset_days
          );

        baseDueDate =
          addSignedDays(
            scheduleSource
              .anchorDate,
            instance
              .schedule_close_offset_days
          );
      } else {
        const groupDeadline =
          groupNumber &&
          setting
            .group_deadlines_enabled ===
              true
            ? (
                Array.isArray(
                  groupDeadlines
                )
                  ? groupDeadlines
                  : []
              ).find(
                row =>
                  String(
                    row.course_structured_instance_id
                  ) ===
                    String(
                      instance.id
                    ) &&
                  Number(
                    row.group_number
                  ) ===
                    Number(
                      groupNumber
                    )
              ) || null
            : null;

        openDate =
          groupDeadline
            ?.open_date ||
          instance.open_date ||
          null;

        baseDueDate =
          groupDeadline
            ?.close_date ||
          instance.close_date ||
          null;
      }

      const effectiveDueDate =
        addExtensionDays(
          baseDueDate,
          extensionDays
        );

      const assignmentLabel =
        getAssignmentLabel(
          assignment,
          structuredAssignments,
          settingsByType
        );

      return {
        id:
          `structured-${instance.id}`,
        structured_form_checkpoint:
          true,
        structured_form_instance_id:
          instance.id,
        structured_form_instance_order:
          instance.instance_order,
        course_assignment_id:
          assignment.id,
        course_id:
          assignment.course_id ||
          courseId,
        assignment_type_id:
          assignment.assignment_type_id,
        assignment_types:
          assignment.assignment_types,
        assignment_display_name:
          assignment
            .assignment_display_name,
        instance_number:
          assignment.instance_number,
        effective_sequence_id:
          setting.sequence_id ||
          null,
        student_assignment_timing_resolved:
          true,
        student_assignment_applicable:
          true,
        open_date:
          openDate,
        due_date:
          baseDueDate,
        close_date:
          baseDueDate,
        effective_open_date:
          openDate,
        effective_base_due_date:
          baseDueDate,
        effective_due_date:
          effectiveDueDate,
        effective_extension_days:
          extensionDays,
        schedule_enabled:
          scheduleEnabled,
        schedule_choice_enabled:
          choiceEnabled,
        schedule_choice_missing:
          choiceMissing,
        schedule_source_label:
          scheduleSource.label ||
          '',
        schedule_anchor_date:
          scheduleSource.anchorDate ||
          null,
        schedule_item_type:
          scheduleItem
            ?.item_type ||
          null,
        course_event_id:
          scheduleItem
            ?.course_event_id ||
          null,
        week_reading_assignment_id:
          scheduleItem
            ?.week_reading_assignment_id ||
          null,
        label:
          `${assignmentLabel} — Part ${instance.instance_order}`
      };
    })
    .filter(Boolean);
}
